import React, { useState } from 'react';
import { Button } from '../shared/Button';
import './YouTubeSearch.css';

interface YouTubeResult {
  id: string;
  title: string;
  channel: string;
  duration: number;
  thumbnail: string;
  url: string;
}

interface VideoInfo {
  id: string;
  title: string;
  artist: string;
  channel: string;
  duration: number;
  thumbnail: string;
  year: number;
}

interface YouTubeSearchProps {
  onSongDownloaded: (song: {
    title: string;
    artist: string;
    year: number;
    audioUrl: string;
    imageUrl?: string;
    youtubeVideoId?: string;
  }) => void;
}

const API_BASE = (process.env.REACT_APP_API_URL || '') + '/api';

const formatDuration = (seconds: number): string => {
  if (!seconds) return '--:--';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export const YouTubeSearch: React.FC<YouTubeSearchProps> = ({ onSongDownloaded }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<YouTubeResult[]>([]);
  const [selectedVideo, setSelectedVideo] = useState<VideoInfo | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isLoadingInfo, setIsLoadingInfo] = useState(false);
  const [error, setError] = useState('');
  const [downloadStatus, setDownloadStatus] = useState('');
  
  // Form fields for editing
  const [editTitle, setEditTitle] = useState('');
  const [editArtist, setEditArtist] = useState('');
  const [editYear, setEditYear] = useState('');

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setIsSearching(true);
    setError('');
    setResults([]);
    setSelectedVideo(null);

    try {
      const response = await fetch(`${API_BASE}/youtube/search?query=${encodeURIComponent(searchQuery)}`);
      const data = await response.json();
      
      if (data.error) {
        setError(data.error);
      } else {
        setResults(data.results || []);
      }
    } catch (err) {
      setError('Kon niet verbinden met de server. Zorg dat de backend draait (npm run server).');
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectVideo = async (video: YouTubeResult) => {
    setIsLoadingInfo(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE}/youtube/info?url=${encodeURIComponent(video.url)}`);
      const data = await response.json();
      
      if (data.error) {
        setError(data.error);
      } else {
        setSelectedVideo(data);
        // Pre-fill the form
        setEditTitle(data.title || '');
        setEditArtist(data.artist || data.channel || '');
        setEditYear(data.year?.toString() || new Date().getFullYear().toString());
      }
    } catch (err) {
      setError('Kon video info niet ophalen.');
      // Use basic info from search result
      setSelectedVideo({
        id: video.id,
        title: video.title,
        artist: video.channel,
        channel: video.channel,
        duration: video.duration,
        thumbnail: video.thumbnail,
        year: new Date().getFullYear()
      });
      setEditTitle(video.title);
      setEditArtist(video.channel);
      setEditYear(new Date().getFullYear().toString());
    } finally {
      setIsLoadingInfo(false);
    }
  };

  const handleDownload = async () => {
    if (!selectedVideo) return;

    setIsDownloading(true);
    setError('');
    setDownloadStatus('Audio downloaden van YouTube...');

    try {
      const response = await fetch(`${API_BASE}/youtube/download`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          url: `https://www.youtube.com/watch?v=${selectedVideo.id}`,
          title: editTitle
        }),
      });

      const data = await response.json();

      if (data.error) {
        setError(`Download mislukt: ${data.error}`);
        setDownloadStatus('');
      } else {
        setDownloadStatus('Download compleet! Toevoegen aan playlist...');
        
        // Add to playlist with YouTube video ID for embedding
        onSongDownloaded({
          title: editTitle,
          artist: editArtist,
          year: parseInt(editYear) || new Date().getFullYear(),
          audioUrl: data.url,
          imageUrl: selectedVideo.thumbnail,
          youtubeVideoId: selectedVideo.id
        });

        // Reset state
        setSelectedVideo(null);
        setSearchQuery('');
        setResults([]);
        setDownloadStatus('✓ Nummer toegevoegd aan playlist!');
        
        setTimeout(() => setDownloadStatus(''), 3000);
      }
    } catch (err) {
      setError('Download mislukt. Controleer of yt-dlp is geïnstalleerd.');
      setDownloadStatus('');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div className="youtube-search">
      <h3>🎵 YouTube Audio Downloaden</h3>
      
      <div className="search-box">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Zoek op artiest, titel of plak YouTube URL..."
          disabled={isSearching || isDownloading}
        />
        <Button
          variant="primary"
          onClick={handleSearch}
          disabled={isSearching || !searchQuery.trim()}
        >
          {isSearching ? '⏳ Zoeken...' : '🔍 Zoeken'}
        </Button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {downloadStatus && <div className="status-message">{downloadStatus}</div>}

      {/* Search Results */}
      {results.length > 0 && !selectedVideo && (
        <div className="search-results">
          <h4>Zoekresultaten</h4>
          {results.map((video) => (
            <div
              key={video.id}
              className="result-item"
              onClick={() => handleSelectVideo(video)}
            >
              <img src={video.thumbnail} alt={video.title} className="result-thumb" />
              <div className="result-info">
                <div className="result-title">{video.title}</div>
                <div className="result-channel">
                  {video.channel} • {formatDuration(video.duration)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Loading video info */}
      {isLoadingInfo && (
        <div className="loading-info">
          <span>⏳ Video informatie laden...</span>
        </div>
      )}

      {/* Selected Video - Edit & Download */}
      {selectedVideo && !isLoadingInfo && (
        <div className="selected-video">
          <div className="selected-header">
            <img src={selectedVideo.thumbnail} alt={selectedVideo.title} className="selected-thumb" />
            <div className="selected-info">
              <div className="selected-title">{selectedVideo.title}</div>
              <div className="selected-channel">{selectedVideo.channel}</div>
            </div>
            <button className="close-btn" onClick={() => setSelectedVideo(null)}>×</button>
          </div>

          <div className="edit-form">
            <div className="form-row">
              <label>Titel</label>
              <input
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                disabled={isDownloading}
              />
            </div>
            <div className="form-row">
              <label>Artiest</label>
              <input
                type="text"
                value={editArtist}
                onChange={(e) => setEditArtist(e.target.value)}
                disabled={isDownloading}
              />
            </div>
            <div className="form-row">
              <label>Jaartal</label>
              <input
                type="number"
                value={editYear}
                onChange={(e) => setEditYear(e.target.value)}
                min="1900"
                max={new Date().getFullYear()}
                disabled={isDownloading}
              />
            </div>
          </div>

          <Button
            variant="success"
            size="large"
            fullWidth
            onClick={handleDownload}
            disabled={isDownloading || !editTitle || !editArtist || !editYear}
          >
            {isDownloading ? '⏳ Downloaden...' : '⬇️ Download & Toevoegen aan Playlist'}
          </Button>
        </div>
      )}

      <div className="youtube-help">
        <small>
          💡 Tip: Zorg dat <code>yt-dlp</code> is geïnstalleerd en de backend server draait.
        </small>
      </div>
    </div>
  );
};
